/**
 * Image utility functions for upload, validation, and processing
 */

import React from 'react';
import { FILE_TYPE } from '@app/consts/common_consts';
import fileDocIcon from '@src/assets/imgs/icons/files/File--DOC.svg';
import fileIfcIcon from '@src/assets/imgs/icons/files/File--IFC.svg';
import fileJpgIcon from '@src/assets/imgs/icons/files/File--JPG.svg';
import filePdfIcon from '@src/assets/imgs/icons/files/File--PDF.svg';
import filePptIcon from '@src/assets/imgs/icons/files/File--PPT.svg';
import fileSvgIcon from '@src/assets/imgs/icons/files/File--SVG.svg';
import fileTxtIcon from '@src/assets/imgs/icons/files/File--TXT.svg';
import fileXlsIcon from '@src/assets/imgs/icons/files/File--XLS.svg';
import fileSlideIcon from '@src/assets/imgs/icons/files/File--Slide.svg';
import fileSheetIcon from '@src/assets/imgs/icons/files/File--Sheet.svg';
import fileImageIcon from '@src/assets/imgs/icons/files/File--Image.svg';
import fileDocumentIcon from '@src/assets/imgs/icons/files/File--Document.svg';
import fileJsonIcon from '@src/assets/imgs/icons/files/File--Json.svg';
import fileOtherIcon from '@src/assets/imgs/icons/files/File--Other.svg';

export interface ImageUploadOptions {
  maxSizeInMB?: number;
  allowedTypes?: string[];
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1 for JPEG compression
}

export interface ImageValidationResult {
  valid: boolean;
  error?: string;
}

export interface ProcessedImage {
  file: File;
  dataUrl: string;
  width: number;
  height: number;
  size: number;
}

export class ImgUtils {
  /**
   * Default allowed image types
   */
  private static readonly DEFAULT_ALLOWED_TYPES = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
  ];

  /**
   * Default max file size in MB
   */
  private static readonly DEFAULT_MAX_SIZE_MB = 5;

  /**
   * Validate image file
   */
  static validateImage(file: File, options: ImageUploadOptions = {}): ImageValidationResult {
    const { maxSizeInMB = this.DEFAULT_MAX_SIZE_MB, allowedTypes = this.DEFAULT_ALLOWED_TYPES } = options;

    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file type. Allowed types: ${allowedTypes.join(', ')}`,
      };
    }

    // Check file size
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return {
        valid: false,
        error: `File size exceeds ${maxSizeInMB}MB limit`,
      };
    }

    return { valid: true };
  }

  /**
   * Convert file to base64 data URL
   */
  static fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Load image and get dimensions
   */
  static loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    });
  }

  /**
   * Resize image if it exceeds max dimensions
   */
  static async resizeImage(file: File, maxWidth: number, maxHeight: number, quality: number = 0.9): Promise<File> {
    const dataUrl = await this.fileToDataUrl(file);
    const img = await this.loadImage(dataUrl);

    // Check if resize is needed
    if (img.width <= maxWidth && img.height <= maxHeight) {
      return file;
    }

    // Calculate new dimensions
    let newWidth = img.width;
    let newHeight = img.height;

    if (img.width > maxWidth) {
      newWidth = maxWidth;
      newHeight = (img.height * maxWidth) / img.width;
    }

    if (newHeight > maxHeight) {
      newHeight = maxHeight;
      newWidth = (img.width * maxHeight) / img.height;
    }

    // Create canvas and resize
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    // Convert canvas to blob
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }
          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        file.type,
        quality
      );
    });
  }

  /**
   * Process and validate image file
   */
  static async processImage(file: File, options: ImageUploadOptions = {}): Promise<ProcessedImage> {
    // Validate
    const validation = this.validateImage(file, options);
    if (!validation.valid) {
      throw new Error(validation.error);
    }

    // Resize if needed
    let processedFile = file;
    if (options.maxWidth || options.maxHeight) {
      processedFile = await this.resizeImage(
        file,
        options.maxWidth || Infinity,
        options.maxHeight || Infinity,
        options.quality
      );
    }

    // Get data URL
    const dataUrl = await this.fileToDataUrl(processedFile);

    // Get dimensions
    const img = await this.loadImage(dataUrl);

    return {
      file: processedFile,
      dataUrl,
      width: img.width,
      height: img.height,
      size: processedFile.size,
    };
  }

  /**
   * Process multiple images
   */
  static async processImages(files: File[], options: ImageUploadOptions = {}): Promise<ProcessedImage[]> {
    const promises = files.map((file) => this.processImage(file, options));
    return Promise.all(promises);
  }

  /**
   * Create thumbnail from image file
   */
  static async createThumbnail(
    file: File,
    width: number = 150,
    height: number = 150,
    quality: number = 0.8
  ): Promise<string> {
    const resized = await this.resizeImage(file, width, height, quality);
    return this.fileToDataUrl(resized);
  }

  /**
   * Convert data URL to File
   */
  static dataUrlToFile(dataUrl: string, filename: string): File {
    const arr = dataUrl.split(',');
    const mime = new RegExp(/:(.*?);/).exec(arr[0])?.[1] || 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.codePointAt(n) || 0;
    }
    return new File([u8arr], filename, { type: mime });
  }

  /**
   * Format file size to human readable string
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Get image type from file
   */
  static getImageType(file: File): string {
    return file.type.split('/')[1].toUpperCase();
  }

  /**
   * Check if file is an image
   */
  static isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Compress image file
   */
  static async compressImage(file: File, quality: number = 0.7): Promise<File> {
    const dataUrl = await this.fileToDataUrl(file);
    const img = await this.loadImage(dataUrl);

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    ctx.drawImage(img, 0, 0);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }
          const compressedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    });
  }

  /**
   * Create image preview URL (revokable)
   */
  static createPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  /**
   * Revoke preview URL to free memory
   */
  static revokePreviewUrl(url: string): void {
    URL.revokeObjectURL(url);
  }

  /**
   * Handle file input change event
   */
  static handleFileInputChange(
    event: React.ChangeEvent<HTMLInputElement>,
    options: ImageUploadOptions = {}
  ): Promise<ProcessedImage[]> {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return Promise.resolve([]);
    }

    return this.processImages(Array.from(files), options);
  }

  /**
   * Create upload FormData for API
   */
  static createUploadFormData(
    files: File[],
    fieldName: string = 'images',
    additionalData?: Record<string, any>
  ): FormData {
    const formData = new FormData();

    files.forEach((file, index) => {
      formData.append(`${fieldName}[${index}]`, file);
    });

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
      });
    }

    return formData;
  }
}
// Returns an <img> element for the given file type
export const getFileIcon = (type: FILE_TYPE) => {
  const iconMap: Record<FILE_TYPE, string> = {
    // Spreadsheet
    [FILE_TYPE.XLS]: fileXlsIcon,
    [FILE_TYPE.XLSX]: fileSheetIcon,
    [FILE_TYPE.XLSM]: fileSheetIcon,
    [FILE_TYPE.CSV]: fileSheetIcon,
    // Presentation
    [FILE_TYPE.PPT]: filePptIcon,
    [FILE_TYPE.PPTX]: fileSlideIcon,
    [FILE_TYPE.GSLIDE]: fileSlideIcon,
    [FILE_TYPE.GSLIDES]: fileSlideIcon,
    // Image
    [FILE_TYPE.JPG]: fileJpgIcon,
    [FILE_TYPE.JPEG]: fileImageIcon,
    [FILE_TYPE.PNG]: fileImageIcon,
    [FILE_TYPE.RAW]: fileImageIcon,
    [FILE_TYPE.SVG]: fileSvgIcon,
    // Document
    [FILE_TYPE.PDF]: filePdfIcon,
    [FILE_TYPE.DOC]: fileDocIcon,
    [FILE_TYPE.DOCX]: fileDocumentIcon,
    [FILE_TYPE.GDOC]: fileDocumentIcon,
    // Data
    [FILE_TYPE.JSON]: fileJsonIcon,
    // CAD / BIM
    [FILE_TYPE.IFC]: fileIfcIcon,
    [FILE_TYPE.DWG]: fileOtherIcon,
    [FILE_TYPE.PLA]: fileOtherIcon,
    [FILE_TYPE.RVT]: fileOtherIcon,
    [FILE_TYPE.THREEDM]: fileOtherIcon,
    [FILE_TYPE.SKP]: fileOtherIcon,
    [FILE_TYPE.STB]: fileOtherIcon,
    // Fallback
    [FILE_TYPE.EXCEL]: fileSheetIcon,
    [FILE_TYPE.OTHER]: fileOtherIcon,
  };
  const src = iconMap[type] ?? fileOtherIcon;
  return <img src={src} alt={type} width={20} height={20} />;
};

export default ImgUtils;
